import { ChangeEvent, useContext, useMemo, useState } from "react";
import CompanyService from "../../../../services/CompanyService";
import SegmentService from "../../../../services/SegmentService";
import { TranslationService } from "../../../../services/TranslationService";
import CriteriaContext, {
  CriterionModel,
  CriterionType
} from "../../providers/CriteriaContext";
import {
  AdditionalDefinition,
  AdditionalDefinitionItems,
  CriterionDefinition,
  ExtraDefinitions
} from "../Criterion";

export const useCriterionLogic = (
  data: CriterionModel,
  extraDefinitions: ExtraDefinitions
) => {
  const { translate } = TranslationService;
  const { updateCriterion, deleteCriterion } = useContext(CriteriaContext);
  const { clientDefs, criterionList, invoiceDefs } = extraDefinitions;

  const [criterion, setCriterion] = useState(
    criterionList.find((item) => item.id === data.Field)!
  );
  const [activityTypes, setActivityTypes] = useState(data.ActivityTypes || "");
  const [inputResetKey, setInputResetKey] = useState(0);

  const criterionIsNumber = [6, 19, 7, 20].includes(criterion?.id);

  const cleanValueIds = () => {
    updateCriterion(data.key, "Value", "");
    updateCriterion(data.key, "IDs", "");
    setInputResetKey((k) => k + 1);
  };

  const handleCriterion = (criterion?: CriterionDefinition) => {
    cleanValueIds();
    if (criterion === undefined) {
      updateCriterion(data.key, "Field", 0);
      return;
    }
    if (
      [
        CriterionType.Number_Amount,
        CriterionType.Date,
        CriterionType.List
      ].includes(criterion.type)
    ) {
      updateCriterion(data.key, "Operator", 0);
    } else if (
      [CriterionType.Invoice, CriterionType.Client].includes(criterion.type)
    ) {
      updateCriterion(data.key, "Data", 0);
      updateCriterion(data.key, "Operator", 3);
      handleDefinition(
        (criterion?.type === CriterionType.Client ? clientDefs : invoiceDefs)[0]
      );
    }
    updateCriterion(data.key, "Field", criterion.id);
    setCriterion(criterion);
  };

  const handleDefinition = (definition: AdditionalDefinition) => {
    cleanValueIds();
    if (
      [
        CriterionType.Number_Amount,
        CriterionType.Date,
        CriterionType.List
      ].includes(definition.type)
    ) {
      updateCriterion(data.key, "Operator", 0);
    }
    updateCriterion(data.key, "Data", definition.AdditionalDefinitionID);
  };

  const handleOperator = (value: number) => {
    cleanValueIds();
    updateCriterion(data.key, "Operator", value);
  };

  const handleValue = (e: ChangeEvent<HTMLInputElement>) => {
    updateCriterion(data.key, "Value", e.target.value);
  };

  const handleAndOr = (ConditionOR: boolean) => {
    updateCriterion(data.key, "ConditionOR", ConditionOR);
  };

  const handleList = (
    value: string[] | undefined,
    items: { value: string; label: string }[]
  ) => {
    if (!value) {
      updateCriterion(data.key, "IDs", "");
      return;
    }
    const label = items.find((el) => el.value === value[0])?.label;
    const valueIds = [...value].join(",");
    updateCriterion(data.key, "IDs", valueIds);
    label &&
      updateCriterion(
        data.key,
        "Value",
        data.Data < 0 ? value[0].toString() : label
      );
  };

  const handleActivityOperator = (value: number) => {
    let formated = (activityTypes || "").replace("!", "");
    if (value === 5) {
      formated = "!" + formated;
    }
    updateCriterion(data.key, "ActivityTypes", formated);
    setActivityTypes(formated);
  };

  const handleActivity = (options: string[] | undefined) => {
    let value = options ? options.join(",") : "";
    if (activityTypes.indexOf("!") > -1) {
      value = "!" + value;
    }
    updateCriterion(data.key, "ActivityTypes", value);
    setActivityTypes(value);
  };

  const {
    dateOperators,
    textOperators,
    amountOperators,
    listOperators,
    ioListOperators,
    dateOperatorsWithEmpty,
    dateOperatorsByDefault
  } = useMemo(() => SegmentService.getOperators(translate), [translate]);

  const currencySymbol = useMemo(
    () =>
      CompanyService.getCurrencies().find(
        (x) => x.CurrencyId === CompanyService.getDefaultCurrencyId()
      )!.Symbol,
    []
  );

  const definitions = useMemo(
    () =>
      (criterion?.type === CriterionType.Client ? clientDefs : invoiceDefs).map(
        (x) => ({ text: x.Name, value: x })
      ),
    [clientDefs, criterion?.type, invoiceDefs]
  );

  const definition = useMemo(
    () =>
      definitions.find((x) => x.value.AdditionalDefinitionID === data.Data)
        ?.value,
    [definitions, data.Data]
  );

  const activities = useMemo(
    () =>
      CompanyService.getActivityTypes().map((x) => ({
        label: x.ActivityTypeName,
        value: x.ActivityTypeID.toString()
      })),
    []
  );

  const getListValue = (
    definition: AdditionalDefinition | undefined
  ): { value: string; label: string }[] => {
    if (definition?.type !== 1) return [];

    const EXCEPTION_ADDITIONAL_LIST = [-9];
    const hasListWithAdditional = EXCEPTION_ADDITIONAL_LIST.includes(
      definition?.AdditionalDefinitionID
    );

    const definitionFind: (
      value: string
    ) => AdditionalDefinitionItems[number] | undefined =
      data?.IDs || hasListWithAdditional
        ? (x) =>
            definition?.AdditionalDefinitionItems.find(
              (y) => y.AdditionalDefinitionItemID.toString() === x
            )
        : (x) =>
            definition?.AdditionalDefinitionItems.find((y) => y.Value === x);

    return (data.IDs ?? data.Value)
      .split(",")
      .filter((x) => x?.length > 0)
      .map(definitionFind)
      .filterFalsey()
      .map((x) => ({
        label: x.Value,
        value: x.AdditionalDefinitionItemID.toString()
      }));
  };

  return {
    criterion,
    criterionIsNumber,
    activityTypes,
    inputResetKey,
    handleCriterion,
    handleDefinition,
    handleOperator,
    handleValue,
    handleAndOr,
    handleList,
    handleActivityOperator,
    handleActivity,
    deleteCriterion,
    dateOperators,
    textOperators,
    amountOperators,
    listOperators,
    ioListOperators,
    dateOperatorsWithEmpty,
    currencySymbol,
    definitions,
    definition,
    activities,
    getListValue,
    dateOperatorsByDefault
  };
};
